<button *ngIf="canUse && !embedded" pButton type="button" icon="pi pi-search" class="p-button-text p-button-rounded gs-trigger"
  [pTooltip]="'globalSearch.trigger' | t" tooltipPosition="bottom" (click)="open()"></button>

<!-- Shared results content — rendered once, reused by both the standalone dialog below (own
     input/header) and the embedded mode (shell owns the input/header, this is mounted inside
     app-unified-search's Records tab instead). Keeping this as ONE template means standalone and
     embedded can never silently drift apart. -->
<ng-template #content>
  <button type="button" class="gs-help-toggle" *ngIf="embedded" [class.gs-help-toggle--on]="showHelp" (click)="toggleHelp()"
    [pTooltip]="'globalSearch.help.toggleTooltip' | t" tooltipPosition="bottom">
    <i class="pi pi-question-circle"></i>
  </button>

  <div class="gs-body">
    <!-- ── Help — what can be searched and how ─────────────────────────────────────────── -->
    <div class="gs-help" *ngIf="showHelp">
      <div class="gs-help-text">{{ 'globalSearch.help.intro' | t }}</div>
      <div class="gs-help-category" *ngFor="let cat of helpCategories">
        <i [ngClass]="cat.icon"></i>
        <span>{{ cat.key | t }}</span>
      </div>
      <div class="gs-help-text gs-help-shortcut">{{ 'globalSearch.help.shortcutText' | t }}</div>
      <button type="button" class="gs-help-all-shortcuts" (click)="openAllShortcuts()">
        <i class="pi pi-th-large"></i> {{ 'globalSearch.help.viewAllShortcuts' | t }}
      </button>
    </div>

    <!-- ── Recent searches — shown only for an empty query, help closed ────────────────────── -->
    <div class="gs-recent" *ngIf="!showHelp && showRecent">
      <div class="gs-group-title">{{ 'globalSearch.recentTitle' | t }}</div>
      <div class="gs-recent-pills">
        <button type="button" class="gs-chip" *ngFor="let text of recentSearches" (click)="useRecentSearch(text)">
          {{ text }}
          <i class="pi pi-times gs-chip-remove" (click)="removeRecentSearch(text, $event)"></i>
        </button>
      </div>
    </div>

    <!-- ── Inline math (macOS Spotlight-style) — only relevant standalone; embedded mode routes a
         pure math expression to Calculator mode itself before this component ever sees it. ───── -->
    <button type="button" class="gs-math-result" *ngIf="!embedded && !showHelp && mathResult !== null"
      (click)="openCalculatorFromMath()">
      <span class="gs-math-value">= {{ mathResult }}</span>
      <span class="gs-math-hint">{{ 'globalSearch.mathOpenHint' | t }}</span>
    </button>

    <div class="gs-hint" *ngIf="!showHelp && minCharsHint">{{ 'globalSearch.minChars' | t }}</div>

    <div class="gs-loading" *ngIf="!showHelp && searching">
      <i class="pi pi-spin pi-spinner"></i> {{ 'globalSearch.searching' | t }}
    </div>

    <div class="gs-empty" *ngIf="!showHelp && mathResult === null && !searching && searched && !hasResults">
      {{ 'globalSearch.noResults' | t }}
    </div>

    <div class="gs-results" *ngIf="!showHelp && !searching && hasResults">
      <div class="gs-group" *ngFor="let group of groups">
        <div class="gs-group-title">{{ group.categoryKey | t }}</div>
        <button type="button" class="gs-result-row" *ngFor="let item of group.items"
          [class.gs-result-row--active]="isHighlighted(item)"
          (mouseenter)="highlightedIndex = flatResults.indexOf(item)"
          (click)="select(item)">
          <i class="gs-result-icon" [ngClass]="group.icon"></i>
          <span class="gs-result-text">
            <span class="gs-result-title">{{ item.title }}</span>
            <span class="gs-result-subtitle" *ngIf="item.subtitle">{{ item.subtitle }}</span>
          </span>
          <span class="gs-result-meta" *ngIf="item.amount != null">
            <span class="gs-result-amount">{{ item.amount | number:'1.0-2' }}</span>
            <p-tag *ngIf="item.paymentStatus" [value]="item.paymentStatus"
              [severity]="paymentStatusSeverity(item.paymentStatus)" styleClass="gs-result-status-tag"></p-tag>
          </span>
        </button>
      </div>
    </div>

    <!-- ── "Ask Linc" fallback card — opens the AI Chat widget with the typed text pre-filled
         (not auto-sent), regardless of whether real record matches were also found above. ───── -->
    <div class="gs-ask-linc-card" *ngIf="!showHelp && !searching && searched && mathResult === null && queryText.trim().length >= 2"
      (click)="askLinc()">
      <i class="pi pi-sparkles"></i>
      <div class="gs-ask-linc-text">
        <div class="gs-ask-linc-title">{{ 'globalSearch.askLinc.cta' | t }}</div>
        <div class="gs-ask-linc-subtitle">{{ 'globalSearch.askLinc.subtitle' | t }}</div>
      </div>
      <i class="pi pi-arrow-right"></i>
    </div>
  </div>
</ng-template>

<p-dialog *ngIf="canUse && !embedded" [(visible)]="visible" [modal]="true" [closable]="true" (onHide)="close()"
  [style]="{width:'560px'}" [breakpoints]="{'600px':'95vw'}" styleClass="gs-dialog" [showHeader]="false"
  appendTo="body">

  <div class="gs-input-row">
    <span class="gs-input-icon-badge"><i class="pi pi-search"></i></span>
    <input pInputText autofocus class="gs-input" [(ngModel)]="queryText" (ngModelChange)="onTextChange()"
      (keydown)="onInputKeydown($event)" [placeholder]="'globalSearch.placeholder' | t" />
    <span class="gs-shortcut-hint">{{ 'globalSearch.shortcutHint' | t }}</span>
    <button type="button" class="gs-help-toggle" (click)="openCalculator()"
      [pTooltip]="'globalSearch.calculatorTooltip' | t" tooltipPosition="bottom">
      <i class="pi pi-calculator"></i>
    </button>
    <span class="gs-close" (click)="close()">ESC</span>
  </div>

  <ng-container *ngTemplateOutlet="content"></ng-container>
</p-dialog>

<div *ngIf="embedded" class="gs-embedded">
  <ng-container *ngTemplateOutlet="content"></ng-container>
</div>

<!-- Standalone-only nested calculator — embedded mode has its own Calculator mode at the shell
     level instead, so this never renders there. -->
<p-dialog *ngIf="canUse && !embedded" [(visible)]="calculatorDialogVisible" [modal]="true" [closable]="true"
  (onHide)="closeCalculator()" [style]="{width:'314px'}" [breakpoints]="{'380px':'92vw'}"
  styleClass="gs-calc-dialog" [showHeader]="false" appendTo="body">
  <app-simple-calculator [active]="calculatorDialogVisible" [startValue]="calculatorPrefill"
    (closeRequested)="closeCalculator()">
  </app-simple-calculator>
</p-dialog>
